
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { CalendarSettings } from "@/lib/supabase";
import { dbToCalendarSettings } from "./calendar-settings-mapper";
import { useGlobalCache, createSupabaseSubscription } from "@/hooks/use-global-cache";

export const useCalendarFetch = (
  user: any | null,
  isClientView: boolean,
  defaultSettings: CalendarSettings
) => {
  const [calendarSettings, setCalendarSettings] = useState<CalendarSettings>(defaultSettings);
  const { toast } = useToast();
  
  // Creiamo una chiave univoca per la cache
  const cacheKey = isClientView 
    ? 'calendar_settings_client' 
    : `calendar_settings_${user?.id || 'anonymous'}`;
  
  // Utilizziamo useGlobalCache per memorizzare le impostazioni del calendario
  const { data: fetchedSettings, isLoading } = useGlobalCache<CalendarSettings | null>(
    cacheKey,
    async () => {
      if (!user) {
        return null;
      }
      
      try {
        let settingsQuery;
        
        if (isClientView) {
          const { data: adminData, error: adminError } = await supabase
            .from("profiles")
            .select("id")
            .eq("role", "admin")
            .limit(1)
            .single();
            
          if (adminError) {
            console.error("Errore nel trovare l'admin:", adminError);
            toast({
              title: "Errore",
              description: "Impossibile caricare le impostazioni del calendario",
              variant: "destructive"
            });
            return null;
          }
          
          settingsQuery = supabase
            .from("calendar_settings")
            .select("*")
            .eq("user_id", adminData.id)
            .maybeSingle();
        } else {
          settingsQuery = supabase
            .from("calendar_settings")
            .select("*")
            .eq("user_id", user.id)
            .maybeSingle();
        }

        const { data, error } = await settingsQuery;

        if (error) {
          console.error("Errore nel caricamento delle impostazioni:", error);
          toast({
            title: "Errore",
            description: "Impossibile caricare le impostazioni del calendario",
            variant: "destructive"
          });
          return null;
        }

        if (data) {
          return dbToCalendarSettings(data);
        }
        
        return null;
      } catch (error) {
        console.error("Errore imprevisto:", error);
        return null;
      }
    },
    {
      validity: 24 * 60 * 60 * 1000, // 24 ore (cache più lunga)
      staleTime: 24 * 60 * 60 * 1000, // 24 ore di stale time (aumentato significativamente)
      setupRealtimeSubscription: (notifyUpdate) => {
        return createSupabaseSubscription(
          supabase,
          'calendar_settings',
          {},
          notifyUpdate
        );
      }
    }
  );

  // Aggiorna lo stato locale solo quando i dati dalla cache cambiano effettivamente
  useEffect(() => {
    if (fetchedSettings) {
      setCalendarSettings(fetchedSettings);
    }
  }, [fetchedSettings]);

  return {
    calendarSettings,
    setCalendarSettings,
    isLoading
  };
};
